/**
 * Inventory Adapter — Simulated, non-canonical for beta.
 *
 * ⚠️ DEPRECATED AS SOURCE OF TRUTH (Phase 1, 2026-04-28).
 * Canonical physical stock owner is `WarehouseInventoryContext`.
 * This adapter is preserved as a future integration boundary for
 * live BoxHero connectivity. Do not add new consumers.
 *
 * See: src/data/inventory/CANONICAL.md
 *
 * Architecture (when re-activated in a later phase):
 *   InventoryAdapter (interface)
 *     ├── SimulatedInventoryAdapter (current — seed data, mock SKU namespace)
 *     └── BoxHeroInventoryAdapter   (future — live API via edge function)
 */

import { SEED_SKU_CATALOG, type SkuItem } from "./skuMapping";
import { fetchInventoryData, fetchStockLevel, checkSupabaseConnection } from "@/services/inventory.service";

/* ══════════════════════════════════════════════════════════
   Types
   ══════════════════════════════════════════════════════════ */

export type StockStatus = "In Stock" | "Low Stock" | "Out of Stock";

export interface StockLevel {
  sku: string;
  itemName: string;
  quantityOnHand: number;
  quantityReserved: number;
  quantityAvailable: number;
  reorderPoint: number;
  status: StockStatus;
  lastSyncedAt: string;
  source: "simulated" | "boxhero";
}

export interface InventorySnapshot {
  items: StockLevel[];
  lastRefreshedAt: string;
  source: "simulated" | "boxhero";
  connected: boolean;
}

export interface InventoryAdapter {
  getSnapshot(): InventorySnapshot | Promise<InventorySnapshot>;
  getStockLevel(sku: string): StockLevel | undefined | Promise<StockLevel | undefined>;
  getStockLevels(skus: string[]): StockLevel[] | Promise<StockLevel[]>;
  checkAvailability(sku: string, quantityNeeded: number): {
    available: boolean;
    quantityAvailable: number;
    shortage: number;
    status: StockStatus;
  } | Promise<{
    available: boolean;
    quantityAvailable: number;
    shortage: number;
    status: StockStatus;
  }>;
  isConnected(): boolean;
}

/* ══════════════════════════════════════════════════════════
   Simulated Inventory Data (enriched from seed)
   ══════════════════════════════════════════════════════════ */

const SIMULATED_STOCK: Record<string, { onHand: number; reserved: number; reorderPoint: number }> = {
  "BASE-SEN-3060": { onHand: 8, reserved: 2, reorderPoint: 3 },
  "BASE-SEN-3260": { onHand: 3, reserved: 1, reorderPoint: 2 },
  "BASE-SEN-3648": { onHand: 2, reserved: 0, reorderPoint: 2 },
  "BASE-SEN-3660": { onHand: 5, reserved: 1, reorderPoint: 3 },
  "BASE-SEN-3672": { onHand: 1, reserved: 0, reorderPoint: 2 },
  "BASE-DEK-3060": { onHand: 4, reserved: 1, reorderPoint: 2 },
  "BASE-DEK-3660": { onHand: 2, reserved: 0, reorderPoint: 2 },
  "BASE-TIL-3060": { onHand: 3, reserved: 1, reorderPoint: 2 },
  "WALL-SEN-STD":  { onHand: 6, reserved: 2, reorderPoint: 3 },
  "WALL-SEN-PRE":  { onHand: 2, reserved: 0, reorderPoint: 2 },
  "WALL-DEK-STD":  { onHand: 3, reserved: 1, reorderPoint: 2 },
  "WALL-TIL-STD":  { onHand: 4, reserved: 1, reorderPoint: 2 },
  "GLS-CLR-STD":   { onHand: 10, reserved: 3, reorderPoint: 4 },
  "GLS-FRO-STD":   { onHand: 5, reserved: 1, reorderPoint: 3 },
  "GLS-CLR-FRM":   { onHand: 7, reserved: 2, reorderPoint: 3 },
  "DOOR-SLD-CHR":  { onHand: 6, reserved: 2, reorderPoint: 3 },
  "DOOR-PIV-CHR":  { onHand: 3, reserved: 1, reorderPoint: 2 },
  "DOOR-SLD-BN":   { onHand: 4, reserved: 1, reorderPoint: 2 },
  "FIX-STD-CHR":   { onHand: 12, reserved: 3, reorderPoint: 5 },
  "FIX-PRE-CHR":   { onHand: 5, reserved: 1, reorderPoint: 3 },
  "FIX-STD-BN":    { onHand: 6, reserved: 2, reorderPoint: 3 },
  "ACC-GRAB-18":   { onHand: 20, reserved: 4, reorderPoint: 8 },
  "ACC-GRAB-24":   { onHand: 15, reserved: 3, reorderPoint: 6 },
  "ACC-GRAB-36":   { onHand: 10, reserved: 2, reorderPoint: 4 },
  "ACC-SEAT-FLD":  { onHand: 4, reserved: 1, reorderPoint: 2 },
  "ACC-SHELF-CRN": { onHand: 8, reserved: 2, reorderPoint: 3 },
  "ACC-NICHE-STD": { onHand: 6, reserved: 1, reorderPoint: 3 },
  "TRIM-SEAL-KIT": { onHand: 25, reserved: 5, reorderPoint: 10 },
  "TRIM-EDGE-CHR": { onHand: 18, reserved: 4, reorderPoint: 8 },
  "HW-MOUNT-KIT":  { onHand: 12, reserved: 3, reorderPoint: 5 },
  "HW-DOOR-KIT":   { onHand: 10, reserved: 2, reorderPoint: 4 },
};

function deriveStockStatus(available: number, reorderPoint: number): StockStatus {
  if (available <= 0) return "Out of Stock";
  if (available <= reorderPoint) return "Low Stock";
  return "In Stock";
}

/* ══════════════════════════════════════════════════════════
   Simulated Adapter
   ══════════════════════════════════════════════════════════ */

export class SimulatedInventoryAdapter implements InventoryAdapter {
  private stockLevels: Map<string, StockLevel>;
  private refreshedAt: string;

  constructor(catalog: SkuItem[] = SEED_SKU_CATALOG) {
    this.refreshedAt = new Date().toISOString();
    this.stockLevels = new Map();

    for (const item of catalog) {
      const sim = SIMULATED_STOCK[item.sku];
      const onHand = sim?.onHand ?? 0;
      const reserved = sim?.reserved ?? 0;
      const available = Math.max(0, onHand - reserved);
      const reorderPoint = sim?.reorderPoint ?? 2;

      this.stockLevels.set(item.sku, {
        sku: item.sku,
        itemName: item.itemName,
        quantityOnHand: onHand,
        quantityReserved: reserved,
        quantityAvailable: available,
        reorderPoint,
        status: deriveStockStatus(available, reorderPoint),
        lastSyncedAt: this.refreshedAt,
        source: "simulated",
      });
    }
  }

  isConnected(): boolean {
    return false; // simulated = not connected to BoxHero
  }

  getSnapshot(): InventorySnapshot {
    return {
      items: Array.from(this.stockLevels.values()),
      lastRefreshedAt: this.refreshedAt,
      source: "simulated",
      connected: false,
    };
  }

  getStockLevel(sku: string): StockLevel | undefined {
    return this.stockLevels.get(sku);
  }

  getStockLevels(skus: string[]): StockLevel[] {
    return skus.map((s) => this.stockLevels.get(s)).filter(Boolean) as StockLevel[];
  }

  checkAvailability(sku: string, quantityNeeded: number) {
    const level = this.stockLevels.get(sku);
    const available = level?.quantityAvailable ?? 0;
    return {
      available: available >= quantityNeeded,
      quantityAvailable: available,
      shortage: Math.max(0, quantityNeeded - available),
      status: level?.status ?? ("Out of Stock" as StockStatus),
    };
  }
}

/* ══════════════════════════════════════════════════════════
   BoxHero Adapter (future — stub for live API)
   ══════════════════════════════════════════════════════════ */

// When BoxHero is connected, this adapter will:
// 1. Call a Lovable Cloud edge function that proxies to BoxHero API
// 2. Cache results locally with TTL
// 3. Support real-time stock updates via webhooks
//
// export class BoxHeroInventoryAdapter implements InventoryAdapter {
//   private apiEndpoint: string;
//   private cache: Map<string, StockLevel>;
//   private cacheTTL: number = 300_000; // 5 minutes
//   
//   constructor(supabaseProjectId: string) {
//     this.apiEndpoint = `https://${supabaseProjectId}.supabase.co/functions/v1/boxhero-sync`;
//     this.cache = new Map();
//   }
//   
//   async refreshFromBoxHero(): Promise<void> {
//     const res = await fetch(this.apiEndpoint, { method: 'POST', body: JSON.stringify({ action: 'sync' }) });
//     const data = await res.json();
//     // Update cache from response
//   }
// }

/* ══════════════════════════════════════════════════════════
   Supabase Inventory Adapter
   ══════════════════════════════════════════════════════════ */

export class SupabaseInventoryAdapter implements InventoryAdapter {
  private cachedSnapshot: InventorySnapshot | null = null;
  private lastFetch: number = 0;
  private cacheTTL: number = 60_000; // 1 minute cache

  private async fetchFromSupabase(): Promise<InventorySnapshot> {
    const now = Date.now();
    
    // Return cached data if still valid
    if (this.cachedSnapshot && (now - this.lastFetch) < this.cacheTTL) {
      return this.cachedSnapshot;
    }

    try {
      const items = await fetchInventoryData();
      
      const snapshot: InventorySnapshot = {
        items,
        lastRefreshedAt: new Date().toISOString(),
        source: "boxhero",
        connected: true,
      };
      
      this.cachedSnapshot = snapshot;
      this.lastFetch = now;
      
      return snapshot;
    } catch (error) {
      console.error("[SupabaseInventoryAdapter] Failed to fetch data:", error);
      // Return empty snapshot on error
      return {
        items: [],
        lastRefreshedAt: new Date().toISOString(),
        source: "boxhero",
        connected: false,
      };
    }
  }

  isConnected(): boolean {
    return true; // Supabase is always "connected" (we try to fetch)
  }

  async getSnapshot(): Promise<InventorySnapshot> {
    return this.fetchFromSupabase();
  }

  async getStockLevel(sku: string): Promise<StockLevel | undefined> {
    try {
      return await fetchStockLevel(sku) ?? undefined;
    } catch {
      return undefined;
    }
  }

  async getStockLevels(skus: string[]): Promise<StockLevel[]> {
    const results: StockLevel[] = [];
    for (const sku of skus) {
      const level = await this.getStockLevel(sku);
      if (level) results.push(level);
    }
    return results;
  }

  async checkAvailability(sku: string, quantityNeeded: number) {
    const level = await this.getStockLevel(sku);
    const available = level?.quantityAvailable ?? 0;
    return {
      available: available >= quantityNeeded,
      quantityAvailable: available,
      shortage: Math.max(0, quantityNeeded - available),
      status: level?.status ?? ("Out of Stock" as StockStatus),
    };
  }
}

/* ══════════════════════════════════════════════════════════
   Singleton & Factory
   ══════════════════════════════════════════════════════════ */

let _adapter: InventoryAdapter | null = null;
let _adapterType: "simulated" | "supabase" = "supabase";

export function getInventoryAdapter(): InventoryAdapter {
  // Use Supabase adapter by default.
  if (!_adapter || _adapterType !== "supabase") {
    _adapter = new SupabaseInventoryAdapter();
    _adapterType = "supabase";
  }
  return _adapter;
}

/** For future use: switch to BoxHero adapter */
export function setInventoryAdapter(adapter: InventoryAdapter): void {
  _adapter = adapter;
}

/* ══════════════════════════════════════════════════════════
   Convenience: Build inventory record for SKU mapping
   ══════════════════════════════════════════════════════════ */

let _cachedInventoryRecord: Record<string, number> = {};

/** 
 * Returns cached inventory record - use for sync operations.
 * For async fetch, use getInventoryRecord() instead.
 */
export function getInventoryRecordSync(): Record<string, number> {
  return _cachedInventoryRecord;
}

/**
 * Update the cached inventory record (called from InventoryContext)
 */
export function setInventoryRecordCache(record: Record<string, number>): void {
  _cachedInventoryRecord = record;
}

/** Returns a Record<sku, availableQty> compatible with generateMaterialList */
export async function getInventoryRecord(): Promise<Record<string, number>> {
  const adapter = getInventoryAdapter();
  const snapshot = await adapter.getSnapshot();
  const record: Record<string, number> = {};
  for (const item of snapshot.items) {
    record[item.sku] = item.quantityAvailable;
  }
  // Update cache
  _cachedInventoryRecord = record;
  return record;
}
